import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/Combobox';
import { formatUserDisplayName } from '../../lib/formatUserDisplayName';
import type { CurrentUser } from '../../types/user';
import { bulkAssignManager, type BulkAssignManagerResult } from './api';

// Stage 5A §3.5 — bulk AM assignment modal. Admin-only (gated by the caller).
// Picks an AM (or Unassigned) and applies it to the selected accounts via the
// bulk endpoint; surfaces the {updated, errors} result before closing.

type Props = {
  accountIds: string[];
  users: CurrentUser[];
  onClose: () => void;
  onDone: (result: BulkAssignManagerResult) => void;
};

const UNASSIGN = '__unassign__';

export function AccountsBulkAssignModal({ accountIds, users, onClose, onDone }: Props) {
  const [choice, setChoice] = useState<string>('');
  const [result, setResult] = useState<BulkAssignManagerResult | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      bulkAssignManager(accountIds, choice === UNASSIGN ? null : choice),
    onSuccess: (res) => {
      setResult(res);
      // Let the parent refetch + clear selection, but keep the modal open so
      // the admin sees the outcome (esp. partial errors).
      onDone(res);
    },
  });

  const chosenUser = users.find((u) => u.uid === choice) ?? null;
  const targetLabel =
    choice === UNASSIGN
      ? 'Unassigned'
      : chosenUser
        ? formatUserDisplayName(chosenUser, { full: true })
        : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-1">Assign account manager</h2>
        <p className="text-sm text-slate-600 mb-4">
          Applies to {accountIds.length} selected account{accountIds.length === 1 ? '' : 's'}.
        </p>

        <label className="block text-sm font-medium text-slate-700 mb-1">Account manager</label>
        <Combobox
          aria-label="Account manager"
          placeholder="Select…"
          searchPlaceholder="Search users…"
          value={choice}
          onValueChange={setChoice}
          disabled={mut.isPending}
          options={[
            { value: UNASSIGN, label: '— Unassign (clear AM) —' },
            ...users.map((u) => ({
              value: u.uid,
              label: formatUserDisplayName(u, { full: true }),
              description: u.email,
              keywords: u.email,
            })),
          ]}
        />

        {targetLabel && !result && (
          <p className="mt-3 text-sm text-slate-700">
            Assign <span className="font-medium">{targetLabel}</span> to{' '}
            <span className="font-medium">{accountIds.length}</span> account
            {accountIds.length === 1 ? '' : 's'}?
          </p>
        )}

        {result && (
          <div className="mt-3 text-sm">
            <p className="text-emerald-700">Updated {result.updated} account{result.updated === 1 ? '' : 's'}.</p>
            {result.errors.length > 0 && (
              <div className="mt-2 text-rose-600">
                <p>{result.errors.length} failed:</p>
                <ul className="list-disc pl-5 max-h-32 overflow-y-auto">
                  {result.errors.map((e) => (
                    <li key={e.accountId}>
                      {e.accountId}: {e.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {mut.isError && (
          <p className="mt-3 text-sm text-rose-600">
            {(mut.error as Error).message || 'Failed to assign. Try again.'}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          {result ? (
            <Button onClick={onClose}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
                Cancel
              </Button>
              <Button onClick={() => mut.mutate()} disabled={choice === '' || mut.isPending}>
                {mut.isPending ? 'Assigning…' : 'Assign'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
